export type AuthReturnRouteId =
  | 'import'
  | 'exchange-sync'
  | 'goals'
  | 'alerts'
  | 'automation'
  | 'settings-notifications'
  | 'wallet-hub'
  | 'transactions'
  | 'tax-optimisation'
  | 'tax-export'
  | 'dashboard'
  | 'portfolio-analytics'
  | 'risk-intelligence'
  | 'rebalancing'
  | 'rebalancing-targets';

export interface AuthReturnContext {
  returnTo: string;
  routeId: AuthReturnRouteId;
  ownerCtaLabel?: string | null;
  proofLabel?: string | null;
  focus?: string | null;
}

interface AuthReturnRouteMeta {
  routeId: AuthReturnRouteId;
  pathname: string;
  routeLabel: string;
  routeName: string;
  loginButtonLabel: string;
  mfaButtonLabel: string;
  ssoButtonLabel: string;
  defaultNextStepLabel: string;
}

const AUTH_RETURN_CONTEXT_STORAGE_KEY = 'auth:return-context';
const AUTH_RETURN_RECOVERED_PARAM = 'authRecovered';

const AUTH_RETURN_ROUTE_META: Record<AuthReturnRouteId, AuthReturnRouteMeta> = {
  import: {
    routeId: 'import',
    pathname: '/import',
    routeLabel: '/import',
    routeName: 'Import transactions',
    loginButtonLabel: 'Sign in to resume import',
    mfaButtonLabel: 'Verify to resume import',
    ssoButtonLabel: 'Continue with SSO to resume import',
    defaultNextStepLabel: 'Resume import proof',
  },
  'exchange-sync': {
    routeId: 'exchange-sync',
    pathname: '/exchange-sync',
    routeLabel: '/exchange-sync',
    routeName: 'Exchange Sync',
    loginButtonLabel: 'Sign in to resume exchange sync',
    mfaButtonLabel: 'Verify to resume exchange sync',
    ssoButtonLabel: 'Continue with SSO to resume exchange sync',
    defaultNextStepLabel: 'Resume exchange sync proof',
  },
  goals: {
    routeId: 'goals',
    pathname: '/goals',
    routeLabel: '/goals',
    routeName: 'Portfolio goals',
    loginButtonLabel: 'Sign in to resume goals',
    mfaButtonLabel: 'Verify to resume goals',
    ssoButtonLabel: 'Continue with SSO to resume goals',
    defaultNextStepLabel: 'Review Drift',
  },
  alerts: {
    routeId: 'alerts',
    pathname: '/alerts',
    routeLabel: '/alerts',
    routeName: 'Alerts',
    loginButtonLabel: 'Sign in to resume alerts',
    mfaButtonLabel: 'Verify to resume alerts',
    ssoButtonLabel: 'Continue with SSO to resume alerts',
    defaultNextStepLabel: 'Resume alerts proof',
  },
  automation: {
    routeId: 'automation',
    pathname: '/automation',
    routeLabel: '/automation',
    routeName: 'Automation',
    loginButtonLabel: 'Sign in to resume automation',
    mfaButtonLabel: 'Verify to resume automation',
    ssoButtonLabel: 'Continue with SSO to resume automation',
    defaultNextStepLabel: 'Resume automation proof',
  },
  'settings-notifications': {
    routeId: 'settings-notifications',
    pathname: '/settings/notifications',
    routeLabel: '/settings/notifications',
    routeName: 'Notification settings',
    loginButtonLabel: 'Sign in to resume notification settings',
    mfaButtonLabel: 'Verify to resume notification settings',
    ssoButtonLabel: 'Continue with SSO to resume notification settings',
    defaultNextStepLabel: 'Resume notification readiness',
  },
  'wallet-hub': {
    routeId: 'wallet-hub',
    pathname: '/wallet-hub',
    routeLabel: '/wallet-hub',
    routeName: 'Wallet Hub',
    loginButtonLabel: 'Sign in to resume Wallet Hub',
    mfaButtonLabel: 'Verify to resume Wallet Hub',
    ssoButtonLabel: 'Continue with SSO to resume Wallet Hub',
    defaultNextStepLabel: 'Review Wallet Hub',
  },
  transactions: {
    routeId: 'transactions',
    pathname: '/transactions',
    routeLabel: '/transactions',
    routeName: 'Transactions',
    loginButtonLabel: 'Sign in to resume transactions',
    mfaButtonLabel: 'Verify to resume transactions',
    ssoButtonLabel: 'Continue with SSO to resume transactions',
    defaultNextStepLabel: 'Review transactions',
  },
  'tax-optimisation': {
    routeId: 'tax-optimisation',
    pathname: '/tax-optimisation',
    routeLabel: '/tax-optimisation',
    routeName: 'Tax optimisation',
    loginButtonLabel: 'Sign in to resume tax optimisation',
    mfaButtonLabel: 'Verify to resume tax optimisation',
    ssoButtonLabel: 'Continue with SSO to resume tax optimisation',
    defaultNextStepLabel: 'Review optimisation first',
  },
  'tax-export': {
    routeId: 'tax-export',
    pathname: '/tax-export',
    routeLabel: '/tax-export',
    routeName: 'Tax export',
    loginButtonLabel: 'Sign in to resume tax export',
    mfaButtonLabel: 'Verify to resume tax export',
    ssoButtonLabel: 'Continue with SSO to resume tax export',
    defaultNextStepLabel: 'Open canonical export owner',
  },
  dashboard: {
    routeId: 'dashboard',
    pathname: '/dashboard',
    routeLabel: '/dashboard',
    routeName: 'Dashboard',
    loginButtonLabel: 'Sign in to resume dashboard',
    mfaButtonLabel: 'Verify to resume dashboard',
    ssoButtonLabel: 'Continue with SSO to resume dashboard',
    defaultNextStepLabel: 'Check Analytics',
  },
  'portfolio-analytics': {
    routeId: 'portfolio-analytics',
    pathname: '/portfolio-analytics',
    routeLabel: '/portfolio-analytics',
    routeName: 'Portfolio analytics',
    loginButtonLabel: 'Sign in to resume analytics',
    mfaButtonLabel: 'Verify to resume analytics',
    ssoButtonLabel: 'Continue with SSO to resume analytics',
    defaultNextStepLabel: 'Check Analytics',
  },
  'risk-intelligence': {
    routeId: 'risk-intelligence',
    pathname: '/risk-intelligence',
    routeLabel: '/risk-intelligence',
    routeName: 'Risk Intelligence',
    loginButtonLabel: 'Sign in to resume risk review',
    mfaButtonLabel: 'Verify to resume risk review',
    ssoButtonLabel: 'Continue with SSO to resume risk review',
    defaultNextStepLabel: 'Resume risk review',
  },
  rebalancing: {
    routeId: 'rebalancing',
    pathname: '/rebalancing',
    routeLabel: '/rebalancing',
    routeName: 'Rebalancing',
    loginButtonLabel: 'Sign in to resume rebalancing',
    mfaButtonLabel: 'Verify to resume rebalancing',
    ssoButtonLabel: 'Continue with SSO to resume rebalancing',
    defaultNextStepLabel: 'Review largest drifts',
  },
  'rebalancing-targets': {
    routeId: 'rebalancing-targets',
    pathname: '/rebalancing-targets',
    routeLabel: '/rebalancing-targets',
    routeName: 'Rebalancing targets',
    loginButtonLabel: 'Sign in to resume rebalancing targets',
    mfaButtonLabel: 'Verify to resume rebalancing targets',
    ssoButtonLabel: 'Continue with SSO to resume rebalancing targets',
    defaultNextStepLabel: 'Save Targets',
  },
};

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function resolveAuthReturnRouteId(pathname: string): AuthReturnRouteId | null {
  if (pathname === '/import') return 'import';
  if (pathname === '/exchange-sync') return 'exchange-sync';
  if (pathname === '/goals') return 'goals';
  if (pathname === '/alerts') return 'alerts';
  if (pathname === '/automation') return 'automation';
  if (pathname === '/settings/notifications') return 'settings-notifications';
  if (pathname === '/wallet-hub') return 'wallet-hub';
  if (pathname === '/transactions') return 'transactions';
  if (pathname === '/tax-optimisation') return 'tax-optimisation';
  if (pathname === '/tax-export') return 'tax-export';
  if (pathname === '/dashboard') return 'dashboard';
  if (pathname === '/portfolio-analytics') return 'portfolio-analytics';
  if (pathname === '/risk-intelligence') return 'risk-intelligence';
  if (pathname === '/rebalancing') return 'rebalancing';
  if (pathname === '/rebalancing-targets') return 'rebalancing-targets';
  return null;
}

export function sanitizeAuthReturnTo(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  try {
    const url = new URL(value, 'http://localhost');
    if (url.origin !== 'http://localhost') {
      return null;
    }

    const normalized = `${url.pathname}${url.search}`;
    return resolveAuthReturnRouteId(url.pathname) ? normalized : null;
  } catch {
    return null;
  }
}

function sanitizeAuthReturnContext(value: unknown): AuthReturnContext | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybeContext = value as Partial<AuthReturnContext>;
  const returnTo = sanitizeAuthReturnTo(maybeContext.returnTo);
  const routeId = returnTo ? resolveAuthReturnRouteId(new URL(returnTo, 'http://localhost').pathname) : null;

  if (!returnTo || !routeId) {
    return null;
  }

  return {
    returnTo,
    routeId,
    ownerCtaLabel:
      typeof maybeContext.ownerCtaLabel === 'string' && maybeContext.ownerCtaLabel.trim()
        ? maybeContext.ownerCtaLabel.trim()
        : null,
    proofLabel:
      typeof maybeContext.proofLabel === 'string' && maybeContext.proofLabel.trim()
        ? maybeContext.proofLabel.trim()
        : null,
    focus:
      typeof maybeContext.focus === 'string' && maybeContext.focus.trim()
        ? maybeContext.focus.trim()
        : null,
  };
}

export function readStoredAuthReturnContext(): AuthReturnContext | null {
  if (!canUseSessionStorage()) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(AUTH_RETURN_CONTEXT_STORAGE_KEY);
    return raw ? sanitizeAuthReturnContext(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function writeAuthReturnContext(context: AuthReturnContext | null) {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    if (!context) {
      window.sessionStorage.removeItem(AUTH_RETURN_CONTEXT_STORAGE_KEY);
      return;
    }

    const sanitized = sanitizeAuthReturnContext(context);
    if (!sanitized) {
      window.sessionStorage.removeItem(AUTH_RETURN_CONTEXT_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(AUTH_RETURN_CONTEXT_STORAGE_KEY, JSON.stringify(sanitized));
  } catch {
    // noop
  }
}

export function clearAuthReturnContext() {
  writeAuthReturnContext(null);
}

export function captureAuthReturnContext({
  pathname,
  search = '',
  ownerCtaLabel,
  proofLabel,
  focus,
}: {
  pathname: string;
  search?: string;
  ownerCtaLabel?: string | null;
  proofLabel?: string | null;
  focus?: string | null;
}): AuthReturnContext | null {
  const routeId = resolveAuthReturnRouteId(pathname);
  const returnTo = sanitizeAuthReturnTo(`${pathname}${search}`);

  if (!routeId || !returnTo) {
    return null;
  }

  const context = sanitizeAuthReturnContext({
    returnTo,
    routeId,
    ownerCtaLabel,
    proofLabel,
    focus,
  });

  writeAuthReturnContext(context);
  return context;
}

export function getAuthReturnContextFromSearch(search: string): AuthReturnContext | null {
  const params = new URLSearchParams(search);
  const returnTo = sanitizeAuthReturnTo(params.get('returnTo'));
  const requestedRouteId = params.get('resumeRoute');
  const focus = params.get('resumeFocus');

  if (!returnTo) {
    return null;
  }

  const resolvedRouteId = resolveAuthReturnRouteId(new URL(returnTo, 'http://localhost').pathname);
  if (!resolvedRouteId || (requestedRouteId && requestedRouteId !== resolvedRouteId)) {
    return null;
  }

  return sanitizeAuthReturnContext({
    returnTo,
    routeId: resolvedRouteId,
    focus,
  });
}

export function syncAuthReturnContextFromSearch(search: string): AuthReturnContext | null {
  const queryContext = getAuthReturnContextFromSearch(search);
  if (queryContext) {
    const storedContext = readStoredAuthReturnContext();
    writeAuthReturnContext({
      ...storedContext,
      ...queryContext,
      ownerCtaLabel: storedContext?.ownerCtaLabel ?? queryContext.ownerCtaLabel ?? null,
      proofLabel: storedContext?.proofLabel ?? queryContext.proofLabel ?? null,
    });
    return readStoredAuthReturnContext();
  }

  return readStoredAuthReturnContext();
}

export function buildLoginPathFromContext(context: AuthReturnContext | null): string {
  if (!context) {
    return '/login';
  }

  const params = new URLSearchParams();
  params.set('returnTo', context.returnTo);
  params.set('resumeRoute', context.routeId);
  if (context.focus) {
    params.set('resumeFocus', context.focus);
  }

  return `/login?${params.toString()}`;
}

export function buildLoginPathForReturn({
  pathname,
  search = '',
  ownerCtaLabel,
  proofLabel,
  focus,
}: {
  pathname: string;
  search?: string;
  ownerCtaLabel?: string | null;
  proofLabel?: string | null;
  focus?: string | null;
}): string {
  return buildLoginPathFromContext(
    captureAuthReturnContext({
      pathname,
      search,
      ownerCtaLabel,
      proofLabel,
      focus,
    }),
  );
}

export function getPostAuthReturnPath(fallback = '/dashboard'): string {
  const context = readStoredAuthReturnContext();
  if (!context) {
    return fallback;
  }

  const url = new URL(context.returnTo, 'http://localhost');
  url.searchParams.set(AUTH_RETURN_RECOVERED_PARAM, '1');
  url.searchParams.set('resumeRoute', context.routeId);
  if (context.focus) {
    url.searchParams.set('resumeFocus', context.focus);
  }

  return `${url.pathname}${url.search}`;
}

export function getAuthReturnMeta(context: AuthReturnContext | null) {
  if (!context) {
    return null;
  }

  const route = AUTH_RETURN_ROUTE_META[context.routeId];

  return {
    ...route,
    ownerCtaLabel: context.ownerCtaLabel ?? route.defaultNextStepLabel,
    proofLabel: context.proofLabel,
  };
}

export function getAuthRecoveryStateFromSearch(pathname: string, search: string) {
  const routeId = resolveAuthReturnRouteId(pathname);
  if (!routeId) {
    return null;
  }

  const params = new URLSearchParams(search);
  if (params.get(AUTH_RETURN_RECOVERED_PARAM) !== '1') {
    return null;
  }

  const requestedRouteId = params.get('resumeRoute');
  if (requestedRouteId && requestedRouteId !== routeId) {
    return null;
  }

  const focus = params.get('resumeFocus');

  return {
    routeId,
    focus: typeof focus === 'string' && focus.trim() ? focus.trim() : null,
  };
}

export function stripAuthRecoveryParams(search: string) {
  const params = new URLSearchParams(search);
  params.delete(AUTH_RETURN_RECOVERED_PARAM);
  params.delete('resumeRoute');
  params.delete('resumeFocus');

  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
}

export function isChunk329AuthRecoveryRoute(pathname: string) {
  const routeId = resolveAuthReturnRouteId(pathname);

  return (
    routeId === 'transactions' ||
    routeId === 'tax-optimisation' ||
    routeId === 'tax-export' ||
    routeId === 'dashboard' ||
    routeId === 'portfolio-analytics' ||
    routeId === 'risk-intelligence' ||
    routeId === 'rebalancing' ||
    routeId === 'rebalancing-targets'
  );
}
